import React, {
    ForwardedRef,
    MutableRefObject,
    RefObject,
    forwardRef,
    useRef,
    useState,
} from "react";

import { FormField, FormLabel } from "@impero-com/ui";
import TextArea, { TextAreaRef } from "antd/es/input/TextArea";
import { cx } from "class-variance-authority";
import { RefCallBack } from "react-hook-form";

import { ERRORED_CLASSNAME } from "../../../utils/form/erroredElements";
import { t } from "../../../utils/i18n";
import { mergeRefs } from "../../../utils/mergeRefs";

import * as theme from "./theme.css";

export interface StringInputBaseProps {
    label?: React.ReactNode;
    disabled?: boolean;
    // The text rendered when the content is an empty string
    placeholder?: string;
    // normal (default): A regular input, with borders, gray-background when disabled
    // borderless: An input without borders, unless focused
    styleVariant?: "normal" | "borderless";
    // Gives focus to this component when mounted
    autoFocus?: boolean;
    // Maximum number of rows (min is 1)
    maxRows?: number;
    onKeyDown?: React.KeyboardEventHandler;
}

export interface StringInputProps extends StringInputBaseProps {
    value: string;
    minLength?: number;
    maxLength?: number;
    required?: boolean;
    onChange?: (value: string) => void;
    onBlur?: (event?: React.FocusEvent<HTMLTextAreaElement>) => void;
    onPressEnter?: (event: React.KeyboardEvent) => void;
    error?: string | undefined;
    fieldRef?: RefCallBack;
}

/**
 * Input component based on antd.TextArea
 * Why a dedicated StringInput?
 * We effectively use a TextArea for all text inputs, to e.g. support multiline, markdown rendering when not in focus
 * This is out of scope for a general purpose input
 */
export const StringInput = forwardRef(
    (
        {
            value,
            onChange,
            onBlur,
            error,
            label,
            placeholder,
            maxRows,
            onPressEnter,
            onKeyDown,
            fieldRef,
            disabled = false,
            styleVariant = "normal",
            autoFocus = false,
            minLength = 0,
            maxLength = 300,
            required = false,
        }: StringInputProps,
        forwardedRef: ForwardedRef<TextAreaRef>
    ) => {
        const [editing, setEditing] = useState(false);
        const internalRef = useRef<TextAreaRef>(null);
        const inputRef: RefObject<TextAreaRef | null> =
            forwardedRef === null
                ? internalRef
                : (forwardedRef as MutableRefObject<TextAreaRef>);

        const displayBorder = styleVariant !== "borderless" || editing;

        const inputClasses = cx(theme.stringInput, {
            [theme.borderless]: !displayBorder,
        });

        const handleOnBlur = (
            event?: React.FocusEvent<HTMLTextAreaElement>
        ) => {
            setEditing(false);
            onBlur?.(event);
        };

        const handlePressEnter = (event: React.KeyboardEvent) => {
            event.preventDefault();
            inputRef.current?.blur();

            if (onPressEnter !== undefined) {
                onPressEnter(event);
            }
        };

        return (
            <FormField
                className={cx(error && ERRORED_CLASSNAME)}
                error={error}
                lite={label === undefined}
            >
                {label && (
                    <FormLabel
                        disabled={disabled}
                        showPencil={styleVariant === "borderless"}
                        onClick={() => inputRef.current?.focus()}
                    >
                        {label}
                    </FormLabel>
                )}
                <TextArea
                    ref={mergeRefs(inputRef, fieldRef)}
                    value={value}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    className={inputClasses}
                    placeholder={
                        placeholder ??
                        (!displayBorder ? t("Form_No_Field_Set") : undefined)
                    }
                    bordered={displayBorder}
                    onChange={(event) =>
                        onChange?.(event.target.value.replaceAll(/\n/g, " "))
                    }
                    onFocus={() => setEditing(true)}
                    onBlur={handleOnBlur}
                    onPressEnter={handlePressEnter}
                    status={error !== undefined ? "error" : ""}
                    minLength={minLength}
                    maxLength={maxLength}
                    required={required}
                    autoSize={{ minRows: 1, maxRows }}
                    spellCheck={false}
                    onKeyDown={onKeyDown}
                />
            </FormField>
        );
    }
);
